import React, { useState, useEffect } from 'react';
import { Box, VStack, Text, Badge, Button, Flex, useBreakpointValue, Tooltip, HStack, Grid } from '@chakra-ui/react';
import { TimeIcon } from '@chakra-ui/icons';
import { formatDateTimeLongInTz, formatTimeWithSecondsInTz } from '../../utils/timezone';
import { useTimezone } from '../../contexts/TimezoneContext';
import {
  Market,
  MarketStatus,
  MarketOutcome,
  MarketType,
  SelectionStatus,
  SelectionOutcome,
  Side,
  Selection
} from '../../types/marketTypes';
import { Event } from '../../types/types';
import {
  isOverUnderMarket,
  marketCardTitle,
  recordedProbabilitiesHistory,
  selectionRows,
  ttSelectionLines,
} from '../../utils/marketUtils';
import { MarketSelectionLines, MarketSelectionRow } from './MarketSelections';
import { ProbabilitiesHistoryModal } from './ProbabilitiesHistoryModal';

interface MarketContentProps {
  market: Market;
  event?: Event;
  compact?: boolean;
  onOpen?: (marketId: number) => void;
  onClose?: (marketId: number) => void;
  onVoid?: (marketId: number) => void;
  onUnvoid?: (marketId: number) => void;
  onSettle?: (market: Market) => void;
  onUnsettle?: (marketId: number) => void;
}

// Helper component for live elapsed time
const LiveElapsedTime: React.FC<{ updatedAt: string; compact?: boolean }> = ({ updatedAt, compact }) => {
  const [elapsedTime, setElapsedTime] = useState('');
  const [tooltipTime, setTooltipTime] = useState('');
  const [timeZone] = useTimezone();

  useEffect(() => {
    const updateElapsedTime = () => {
      const now = new Date();

      let updated: Date;
      const parsedNum = Number(updatedAt);
      if (!isNaN(parsedNum)) {
        updated = new Date(parsedNum);
      } else {
        updated = new Date(updatedAt);
      }

      const diffMs = now.getTime() - updated.getTime();

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      setElapsedTime(formatted);

      setTooltipTime(formatTimeWithSecondsInTz(updated, timeZone));
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [updatedAt, timeZone]);

  return (
    <Tooltip label={tooltipTime} placement="top">
      <Text fontSize={compact ? "2xs" : "xs"} color="gray.500" display={compact ? "none" : "block"}>
        {elapsedTime || 'calculating...'}
      </Text>
    </Tooltip>
  );
};

export const MarketContent: React.FC<MarketContentProps> = ({
  market,
  event,
  compact = false,
  onOpen,
  onClose,
  onVoid,
  onUnvoid,
  onSettle,
  onUnsettle
}) => {
  // State for probabilities history modal
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [timeZone] = useTimezone();

  // Responsive values
  const buttonSize = useBreakpointValue({ base: compact ? '2xs' : 'xs', md: compact ? 'xs' : 'xs' });
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Compact button styling
  const buttonProps = compact ? {
    size: 'xs',
    fontSize: '2xs',
    height: '1.5rem',
    px: 2,
    py: 0,
    minW: 'auto'
  } : {
    fontSize: 'xs'
  };

  // Helper function to get status color
  const getSelectionStatusColor = (status: SelectionStatus) => {
    switch (status) {
      case SelectionStatus.OPEN:
        return 'green';
      case SelectionStatus.SUSPENDED:
        return 'orange';
      case SelectionStatus.CLOSED:
        return 'gray';
      default:
        return 'gray';
    }
  };

  // The lines a market offers at once, each carrying its own status and result; null when it offers none.
  const ttLines = ttSelectionLines(market);
  const probabilitiesHistory = recordedProbabilitiesHistory(market);

  // Group selections by line number for over/under markets
  const groupSelectionsByLine = () => {
    const grouped: Record<string, { over?: Selection; under?: Selection }> = {};

    market.selections.forEach(selection => {
      const match = selection.name.match(/(Over|Under)\s+([\d.]+)/i);
      if (match) {
        const type = match[1].toLowerCase();
        const line = match[2];

        if (!grouped[line]) {
          grouped[line] = {};
        }

        if (type === 'over') {
          grouped[line].over = selection;
        } else {
          grouped[line].under = selection;
        }
      }
    });

    return Object.entries(grouped).sort(([a], [b]) => parseFloat(a) - parseFloat(b));
  };

  const shouldDimMarket = () => {
    const hasHiddenStatus = market.marketStatus === 'HIDDEN' as MarketStatus;
    const totalPrices = market.selections.reduce((sum, sel) => sum + sel.prices.length, 0);
    const allPricesAreOne = totalPrices > 0 && market.selections.every(sel =>
      sel.prices.length > 0 && sel.prices.every(price => price.price === 1.00)
    );
    const allPricesAreZero = totalPrices > 0 && market.selections.every(sel =>
      sel.prices.length > 0 && sel.prices.every(price => price.price === 0)
    );
    return hasHiddenStatus || totalPrices === 0 || allPricesAreOne || allPricesAreZero;
  };

  return (
    <Box
      bg={market.marketStatus === MarketStatus.CLOSED && market.marketOutcome !== MarketOutcome.VOIDED && market.marketOutcome !== MarketOutcome.FINISHED ? 'gray.50' : 'transparent'}
      p={compact ? 1 : 2}
      borderRadius="md"
      opacity={shouldDimMarket() ? 0.5 : 1}
    >
      {/* Probabilities History Modal */}
      <ProbabilitiesHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        marketName={market.name}
        probabilitiesHistory={probabilitiesHistory}
      />
      <VStack align="stretch" spacing={compact ? 0 : 1}>
        <Flex
          gap={2}
          width="100%"
          justify="space-between"
          align="flex-start"
          mb={1}
        >
          <VStack align="start" spacing={1} flex="1" minW="0">
            <Tooltip label={`ID: ${market.id} | ${market.name}`} placement="top">
              <Text
                fontWeight="bold"
                fontSize={compact ? "xs" : "sm"}
                cursor="help"
              >
                {marketCardTitle(market)}
              </Text>
            </Tooltip>
            <Flex gap={1} flexWrap="wrap" align="center">
              <Badge colorScheme={market.marketStatus === MarketStatus.OPEN ? 'green' : 'gray'} fontSize="0.65rem">
                {market.marketStatus}
              </Badge>
              {market.marketStatus === MarketStatus.OPEN && (
                <LiveElapsedTime updatedAt={market.updatedAt} compact={compact} />
              )}
              {market.marketOutcome && (
                <Badge colorScheme={market.marketOutcome === MarketOutcome.VOIDED ? 'red' : 'blue'} fontSize="0.65rem">
                  {market.marketOutcome}
                </Badge>
              )}
            </Flex>
          </VStack>

          <Flex
            gap={1}
            flexWrap="wrap"
            justify="flex-end"
            flexShrink={0}
          >
            {probabilitiesHistory.length > 0 && (
              <Tooltip label="View probability history">
                <Button
                  size="xs"
                  colorScheme="teal"
                  variant="outline"
                  onClick={() => setIsHistoryModalOpen(true)}
                  minW="auto"
                  px={1.5}
                  fontSize="2xs"
                >
                  H
                </Button>
              </Tooltip>
            )}
            {market.marketOutcome === MarketOutcome.VOIDED ?
              (onUnvoid && (
                <Button
                  size={buttonSize}
                  colorScheme="blue"
                  onClick={() => onUnvoid(market.id)}
                  width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                  {...buttonProps}
                >
                  Unvoid
                </Button>
              ))
              : market.marketOutcome === MarketOutcome.FINISHED ?
                (onUnsettle && (
                  <Button
                    size={buttonSize}
                    colorScheme="purple"
                    onClick={() => onUnsettle(market.id)}
                    width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                    {...buttonProps}
                  >
                    Unsettle
                  </Button>
                ))
                : market.marketStatus === MarketStatus.CLOSED ?
                  (<>
                    <Button
                      size={buttonSize}
                      colorScheme="green"
                      onClick={() => onOpen?.(market.id)}
                      width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                      {...buttonProps}
                    >
                      Open
                    </Button>
                    {onVoid && (
                      <Button
                        size={buttonSize}
                        colorScheme="red"
                        onClick={() => onVoid(market.id)}
                        width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                        {...buttonProps}
                      >
                        Void
                      </Button>
                    )}
                    {onSettle && (
                      <Button
                        size={buttonSize}
                        colorScheme="purple"
                        onClick={() => onSettle(market)}
                        width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                        {...buttonProps}
                      >
                        Settle
                      </Button>
                    )}
                  </>)
                  : (market.marketStatus === MarketStatus.OPEN || market.marketStatus === MarketStatus.SUSPENDED) ?
                    (<Button
                      size={buttonSize}
                      colorScheme="gray"
                      onClick={() => onClose?.(market.id)}
                      width={{ base: isMobile && !compact ? '100%' : 'auto', sm: 'auto' }}
                      {...buttonProps}
                    >
                      Close
                    </Button>)
                    : null}
          </Flex>
        </Flex>

        {/* GR8 IDs and closing time */}
        {(market.gr8MarketId || market.gr8PeriodId || market.closedAt) && (
          <Flex gap={2} mt={1} flexWrap="wrap" fontSize="xs" color="gray.600">
            {market.gr8MarketId && (
              <Flex align="center">
                <Text fontWeight="medium">GR8 Market ID:</Text>
                <Text ml={1}>{market.gr8MarketId}</Text>
              </Flex>
            )}
            {market.gr8PeriodId && (
              <Flex align="center" ml={market.gr8MarketId ? 2 : 0}>
                <Text fontWeight="medium">GR8 Period ID:</Text>
                <Text ml={1}>{market.gr8PeriodId}</Text>
              </Flex>
            )}
            {market.closedAt && (
                <Flex align="center">
                  <Text fontWeight="medium">Closed At:</Text>
                  <Text ml={1}>{formatDateTimeLongInTz(market.closedAt, timeZone)}</Text>
                </Flex>
            )}
          </Flex>
        )}

        {ttLines ? (
          <MarketSelectionLines lines={ttLines} compact={compact} />
        ) : isOverUnderMarket(market) ? (
          <VStack align="stretch" spacing={2} mt={2}>
            {groupSelectionsByLine().map(([line, { over, under }]) => (
              <Box key={line} borderWidth="1px" borderRadius="md" p={2}>
                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
                  Line {line}
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  {over && (
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Tooltip label={`Selection ID: ${over.id}`} placement="top">
                          <Text fontSize="xs" fontWeight="semibold" color="blue.600" cursor="help">
                            Over
                          </Text>
                        </Tooltip>
                        <Flex gap={1}>
                          <Badge
                            colorScheme={getSelectionStatusColor(over.status)}
                            fontSize="0.5rem"
                            variant="solid"
                          >
                            {over.status}
                          </Badge>
                          {over.outcome && (
                            <Badge
                              colorScheme={
                                over.outcome === SelectionOutcome.WIN ? 'green' :
                                  over.outcome === SelectionOutcome.LOSE ? 'red' : 'red'
                              }
                              fontSize="0.5rem"
                            >
                              {over.outcome}
                            </Badge>
                          )}
                        </Flex>
                      </Flex>
                      {over.prices.length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          {over.prices.map((price) => (
                            <Flex key={price.id} gap={1} align="center" fontSize={compact ? "xs" : "sm"}>
                              <Badge
                                variant="outline"
                                colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
                                fontSize="0.6rem"
                              >
                                {price.side}
                              </Badge>
                              <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
                              {price.probability !== undefined && (
                                <Text color="gray.600" fontSize="xs">
                                  ({(price.probability * 100).toFixed(1)}%)
                                </Text>
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                  {under && (
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Tooltip label={`Selection ID: ${under.id}`} placement="top">
                          <Text fontSize="xs" fontWeight="semibold" color="red.600" cursor="help">
                            Under
                          </Text>
                        </Tooltip>
                        <Flex gap={1}>
                          <Badge
                            colorScheme={getSelectionStatusColor(under.status)}
                            fontSize="0.5rem"
                            variant="solid"
                          >
                            {under.status}
                          </Badge>
                          {under.outcome && (
                            <Badge
                              colorScheme={
                                under.outcome === SelectionOutcome.WIN ? 'green' :
                                  under.outcome === SelectionOutcome.LOSE ? 'red' : 'red'
                              }
                              fontSize="0.5rem"
                            >
                              {under.outcome}
                            </Badge>
                          )}
                        </Flex>
                      </Flex>
                      {under.prices.length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          {under.prices.map((price) => (
                            <Flex key={price.id} gap={1} align="center" fontSize={compact ? "xs" : "sm"}>
                              <Badge
                                variant="outline"
                                colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
                                fontSize="0.6rem"
                              >
                                {price.side}
                              </Badge>
                              <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
                              {price.probability !== undefined && (
                                <Text color="gray.600" fontSize="xs">
                                  ({(price.probability * 100).toFixed(1)}%)
                                </Text>
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </Grid>
              </Box>
            ))}
          </VStack>
        ) : (
          selectionRows(market, event).map(({ selection, label }) => (
            <MarketSelectionRow
              key={selection.id}
              selection={selection}
              compact={compact}
              label={label}
            />
          ))
        )}
      </VStack>
    </Box>
  );
};
